import React from "react";
import LabelValuePair from "../common/LabelValuePair";
import {
  AuditLog,
  AuditLogType,
  AuditLogTypeLabels,
} from "../../models/AuditLog";

import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { dialogMedium } from "@/lib/dialogSize";

const ViewExportEntitiesAuditLogDetailsModal: React.FC<{
  auditLog: AuditLog | null;
  open: boolean;
  onClose: () => void;
  organizationId: string;
}> = ({ auditLog, open, onClose, organizationId }) => {
  const generateDynamicLink = (entityId: string | null, type: AuditLogType) => {
    if (entityId === null) return `/organization/${organizationId}/audit-logs`;

    switch (type) {
      case AuditLogType.CasesExport:
        return `/organization/${organizationId}/case/${entityId}`;
      case AuditLogType.FirmDocumentsFoldersExport:
        return `/organization/${organizationId}/firm-documents-folder/${entityId}`;
      case AuditLogType.ProvidersExport:
        return `/organization/${organizationId}/provider/${entityId}`;
      case AuditLogType.TemplatesExport:
        return `/organization/${organizationId}/template/${entityId}`;
      case AuditLogType.IntakesExport:
        return `/organization/${organizationId}/intake/${entityId}`;
      default:
        return `/organization/${organizationId}/audit-logs`;
    }
  };

  const getLabel = (eventType: AuditLogType): string => {
    switch (eventType) {
      case AuditLogType.CasesExport:
        return "Case numbers:";
      case AuditLogType.FirmDocumentsFoldersExport:
        return "Folder paths:";
      case AuditLogType.ProvidersExport:
        return "Name ids:";
      case AuditLogType.TemplatesExport:
        return "Template types:";
      case AuditLogType.IntakesExport:
        return "Intake numbers:";
      case AuditLogType.FlaggedPathsExport:
        return "Flagged paths:";
      default:
        return "";
    }
  };

  const parseReason = (additionalData: string) => {
    try {
      const parsedData = JSON.parse(additionalData);
      if (parsedData && typeof parsedData === 'object' && parsedData.Reason) {
        return (
          <span
            className="text-sm whitespace-pre-wrap break-words overflow-y-auto max-h-[15vh] pr-2"
          >
            {parsedData.Reason}
          </span>
        );
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const parseAdditionalData = (
    additionalData: string,
    eventType: AuditLogType
  ) => {
    try {
      const parsedData = JSON.parse(additionalData);
      if (parsedData && typeof parsedData === 'object' && !Array.isArray(parsedData)) {
        switch (eventType) {
          case AuditLogType.CasesExport:
            if (parsedData.Case && Array.isArray(parsedData.Case)) {
              return parsedData.Case.map(
                (caseData: { Id: string; CaseNumber: string }, index: number) => (
                  <span key={`case-${index}`} className="text-sm block">
                    <a
                      href={generateDynamicLink(caseData.Id, eventType)}
                      className="hover:underline text-primary"
                    >
                      {caseData.CaseNumber}
                    </a>
                  </span>
                )
              );
            }
            break;

          case AuditLogType.IntakesExport:
            if (parsedData.Intake && Array.isArray(parsedData.Intake)) {
              return parsedData.Intake.map(
                (intakeData: { Id: string; IntakeNumber: string }, index: number) => (
                  <span key={`intake-${index}`} className="text-sm block">
                    <a
                      href={generateDynamicLink(intakeData.Id, eventType)}
                      className="hover:underline text-primary"
                    >
                      {intakeData.IntakeNumber}
                    </a>
                  </span>
                )
              );
            }
            break;

          case AuditLogType.FirmDocumentsFoldersExport:
            if (parsedData.FirmDocumentsFolder && Array.isArray(parsedData.FirmDocumentsFolder)) {
              return parsedData.FirmDocumentsFolder.map(
                (
                  firmDocumentsFolderData: { Id: string; FolderPath: string },
                  index: number
                ) => (
                  <span key={`folder-${index}`} className="text-sm block">
                    <a
                      href={generateDynamicLink(firmDocumentsFolderData.Id, eventType)}
                      className="hover:underline text-primary"
                    >
                      {firmDocumentsFolderData.FolderPath}
                    </a>
                  </span>
                )
              );
            }
            break;

          case AuditLogType.ProvidersExport:
            if (parsedData.Provider && Array.isArray(parsedData.Provider)) {
              return parsedData.Provider.map(
                (
                  providerData: { Id: string; ProviderNameId: string },
                  index: number
                ) => (
                  <span key={`provider-${index}`} className="text-sm block">
                    <a
                      href={generateDynamicLink(providerData.Id, eventType)}
                      className="hover:underline text-primary"
                    >
                      {providerData.ProviderNameId}
                    </a>
                  </span>
                )
              );
            }
            break;

          case AuditLogType.TemplatesExport:
            if (parsedData.Template && Array.isArray(parsedData.Template)) {
              return parsedData.Template.map(
                (
                  templateData: { Id: string; TemplateId: string },
                  index: number
                ) => (
                  <span key={`template-${index}`} className="text-sm block">
                    <a
                      href={generateDynamicLink(templateData.Id, eventType)}
                      className="hover:underline text-primary"
                    >
                      {templateData.Id}
                    </a>
                  </span>
                )
              );
            }
            break;

            case AuditLogType.FlaggedPathsExport:
              if (parsedData.FlaggedPaths && Array.isArray(parsedData.FlaggedPaths)) {
                return parsedData.FlaggedPaths.map(
                    (
                        flaggedPathData: { Id: string; Path: string },
                        index: number
                    ) => (
                        <span key={`flagged-path-${index}`} className="text-sm">
                          {flaggedPathData.Path}
                        </span>
                    )
                );
              }
            break;

          default:
            return <span className="text-sm">Invalid data format</span>;
        }

        return <span className="text-sm">No data available</span>;
      }

      return <span className="text-sm">Invalid data format</span>;
    } catch (error) {
      return (
        <span className="text-sm">Failed to parse additionalData</span>
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className={`${dialogMedium} overflow-hidden`}>
        {auditLog ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {AuditLogTypeLabels[auditLog.eventType]}
              </DialogTitle>
            </DialogHeader>

            <Separator />

            <div className="m-0">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-bold">
                  Reason:
                </span>
                {auditLog.additionalData ? (
                  parseReason(auditLog.additionalData)
                ) : (
                  <></>
                )}
              </div>
            </div>

            <Separator />

            <div className="m-0">
              <div className="flex flex-col gap-2">
                <span className="text-sm font-bold">
                  {getLabel(auditLog.eventType)}
                </span>
                {auditLog.additionalData ? (
                  parseAdditionalData(auditLog.additionalData, auditLog.eventType)
                ) : (
                  <></>
                )}
              </div>
            </div>

            <Separator />

            <LabelValuePair
              label="Peroformed By"
              value={auditLog.performerEmailAddress}
            />

            <LabelValuePair label="Event Date" value={auditLog.eventDate} />
          </>
        ) : (
          <div />
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewExportEntitiesAuditLogDetailsModal;
